/* The workbook grid - SIM-01..06, shared by the plan and every simulation.
 *
 * Constraint C4 is explicit: "planners know the current grid, so the
 * simulation screen keeps the same sections and row names". So this is the
 * workbook - the same three sections, the same row labels, the weeks across
 * the columns - and a simulation is shown as exactly the same grid as the
 * plan it would replace, with the cells it changes outlined.
 */

import { Fragment, useMemo } from 'react'
import type { GridBody, Status } from '../api/client'
import { StatusChip, statusGlyph } from './StatusChip'

export const LABEL_WIDTH = 230
export const CELL_WIDTH = 58

interface Props {
  grid: GridBody
  /* Working days editable in place (the plan screen). */
  editable?: boolean
  onCellChange?: (crewIndex: number, bucketIndex: number, value: number) => void
  onCellCommit?: (crewIndex: number, bucketIndex: number, raw: string) => void
  onExplain?: (bucketIndex: number) => void
  /* "crew:bucket" -> the value before, for the cells a simulation changes. */
  changed?: Map<string, number>
  /* The DOH of the plan before the simulation, shown under the new one. */
  before?: { doh: number[]; status: Status[] }
}

export function PlanGrid({ grid, editable, onCellChange, onCellCommit, onExplain, changed, before }: Props) {
  const { buckets, monitoring, crews, totals, value_stream: vs } = grid

  /* Which bucket closes each week - the one the status counters read.
     Everything else in a daily week is shown but not counted. */
  const weekGroups = useMemo(() => {
    const groups: { year: number; week: number; count: number; closing: number }[] = []
    buckets.forEach((b) => {
      const last = groups[groups.length - 1]
      if (last && last.week === b.week && last.year === b.year) {
        last.count += 1
        last.closing = b.index
      } else {
        groups.push({ year: b.year, week: b.week, count: 1, closing: b.index })
      }
    })
    return groups
  }, [buckets])

  const closingBuckets = useMemo(() => new Set(weekGroups.map((g) => g.closing)), [weekGroups])

  return (
    <>
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table
            className="mono"
            style={{
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: 12,
              minWidth: LABEL_WIDTH + buckets.length * CELL_WIDTH,
            }}
          >
            <thead>
              <tr>
                <Th sticky>Production week</Th>
                {/* ONE header per week, spanning its buckets: the counters
                    count weeks, and a daily week is seven columns. */}
                {weekGroups.map((g) => (
                  <th
                    key={`${g.year}-${g.week}`}
                    colSpan={g.count}
                    title={
                      g.count > 1
                        ? `Week ${g.week} — ${g.count} daily columns, counted once on the last of them`
                        : `Week ${g.week}`
                    }
                    style={{
                      background: 'var(--surface-3)',
                      borderBottom: '1px solid var(--border-strong)',
                      borderRight: '2px solid var(--border-strong)',
                      padding: '5px 6px',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: 11,
                      whiteSpace: 'nowrap',
                      color: g.count > 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                    }}
                  >
                    {`W${String(g.week).padStart(2, '0')}`}
                    {g.count > 1 && <span className="muted" style={{ fontWeight: 400 }}> · {g.count}d</span>}
                  </th>
                ))}
              </tr>
              <tr>
                <Th sticky>Production date</Th>
                {buckets.map((b) => (
                  <Th key={b.index} center dim>
                    {b.date ? b.date.slice(5) : '—'}
                  </Th>
                ))}
              </tr>
            </thead>

            <tbody>
              <SectionRow label="Monitoring" span={buckets.length + 1} />

              <DataRow label="Open Orders" values={monitoring.open_orders} digits={2} />
              <DataRow label="Demonstrated Capacity" values={monitoring.demonstrated_capacity} digits={2} />
              <DataRow label="Net Balance" values={monitoring.net_balance} digits={2} />
              <DataRow label="Cumulative Balance" values={monitoring.cumulative_balance} digits={2} />

              {/* The row the planner actually reads. */}
              <tr>
                <Td sticky strong>
                  DOH complete pipeline
                </Td>
                {buckets.map((b, t) => (
                  <td
                    key={b.index}
                    title={
                      b.horizon_edge
                        ? 'Horizon edge: no demand left to cover, so the corridor does not apply here'
                        : `${monitoring.status[t]} · corridor ${vs.min_doh}–${vs.max_doh}` +
                          (closingBuckets.has(b.index)
                            ? ` · this is the bucket week ${b.week} is COUNTED on`
                            : ` · shown but not counted — week ${b.week} is counted on its last bucket`) +
                          (onExplain ? ' · click to explain' : '')
                    }
                    onClick={onExplain ? () => onExplain(b.index) : undefined}
                    style={{
                      ...cellStyle,
                      cursor: onExplain ? 'pointer' : undefined,
                      fontWeight: closingBuckets.has(b.index) ? 700 : 400,
                      opacity: closingBuckets.has(b.index) || b.horizon_edge ? 1 : 0.62,
                      background: b.horizon_edge ? 'var(--status-edge-bg)' : statusBg(monitoring.status[t]),
                      color: b.horizon_edge ? 'var(--text-muted)' : 'var(--text-primary)',
                    }}
                  >
                    {b.horizon_edge ? '·' : `${statusGlyph(monitoring.status[t])} ${monitoring.doh[t].toFixed(1)}`}
                  </td>
                ))}
              </tr>

              {before && (
                <tr>
                  <Td sticky>DOH before this simulation</Td>
                  {buckets.map((b, t) => (
                    <td
                      key={b.index}
                      style={{
                        ...cellStyle,
                        color: 'var(--text-secondary)',
                        background: b.horizon_edge ? 'var(--status-edge-bg)' : undefined,
                      }}
                    >
                      {b.horizon_edge ? '·' : `${statusGlyph(before.status[t])} ${before.doh[t].toFixed(1)}`}
                    </td>
                  ))}
                </tr>
              )}

              <DataRow label="MIN Pipeline Inventory (DOH)" values={buckets.map(() => vs.min_doh)} digits={0} dim />
              <DataRow label="MAX Pipeline Inventory (DOH)" values={buckets.map(() => vs.max_doh)} digits={0} dim />
              <DataRow label="Daily Customer consumption" values={monitoring.daily_consumption} digits={0} />
              <DataRow label="Active Inventory (pcs)" values={monitoring.inventory} digits={0} />
              <DataRow label="Weekly Customer Requirement" values={monitoring.demand} digits={0} />

              {crews.map((crew, ci) => (
                <Fragment key={crew.id}>
                  <SectionRow label={crew.name} span={buckets.length + 1} crew />

                  {/* The ONLY editable row in the whole grid. */}
                  <tr>
                    <Td sticky strong>
                      Working days
                    </Td>
                    {buckets.map((b, t) => {
                      const locked = crew.locked[t]
                      const overtime = crew.days[t] > b.plant_days
                      const was = changed?.get(`${ci}:${t}`)
                      const moved = was !== undefined
                      const look: React.CSSProperties = {
                        background: moved ? 'var(--status-warning-bg)' : locked ? 'var(--surface-3)' : undefined,
                        color: overtime ? 'var(--series-2)' : 'var(--text-primary)',
                        fontWeight: overtime || moved ? 700 : 400,
                        boxShadow: moved ? 'inset 0 0 0 2px var(--status-warning)' : undefined,
                      }
                      return (
                        <td key={b.index} style={{ ...cellStyle, padding: 0 }}>
                          {editable ? (
                            <input
                              className="grid-input"
                              value={crew.days[t]}
                              readOnly={locked}
                              onChange={(e) => onCellChange?.(ci, t, Number(e.target.value))}
                              onBlur={(e) => onCellCommit?.(ci, t, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                              }}
                              title={
                                locked
                                  ? 'Locked — no simulation will change this cell'
                                  : `max ${b.max_days} (plant open ${b.plant_days})${overtime ? ' · overtime' : ''}`
                              }
                              style={look}
                            />
                          ) : (
                            <div
                              className="grid-input"
                              title={moved ? `was ${was}` : locked ? 'locked' : undefined}
                              style={{ ...look, display: 'grid', placeItems: 'center' }}
                            >
                              {crew.days[t]}
                            </div>
                          )}
                        </td>
                      )
                    })}
                  </tr>

                  <DataRow label="Install Capacity" values={crew.installed_capacity} digits={0} dim />
                  <DataRow label="Output" values={crew.output} digits={0} />
                  <DataRow label="Crew" values={crew.crew_pct} digits={2} dim />
                  <DataRow label="Efficiency" values={crew.efficiency} digits={2} />
                  <DataRow label="Direct People" values={crew.direct} digits={0} dim />
                  <DataRow label="Indirect People" values={crew.indirect} digits={0} dim />
                  <DataRow label="In Pcs" values={crew.pieces} digits={0} />
                </Fragment>
              ))}

              <SectionRow label="Totals" span={buckets.length + 1} />

              <DataRow label="Number of Standard Shifts" values={totals.standard_shifts} digits={0} dim />
              <DataRow label="Plant days of production" values={buckets.map((b) => b.plant_days)} digits={0} dim />
              <DataRow label="Customer days of production" values={buckets.map((b) => b.customer_days)} digits={0} dim />

              {/* MPW is contractual: a breach is flagged, never silently allowed. */}
              <tr>
                <Td sticky strong>
                  Weekly Production Schedule
                </Td>
                {buckets.map((b, t) => (
                  <td
                    key={b.index}
                    title={totals.over_mpw[t] ? `Above the contracted maximum (${vs.mpw.toLocaleString()}/week)` : undefined}
                    style={{
                      ...cellStyle,
                      background: totals.over_mpw[t] ? 'var(--status-warning-bg)' : undefined,
                      fontWeight: totals.over_mpw[t] ? 600 : 400,
                    }}
                  >
                    {totals.over_mpw[t] ? '▲ ' : ''}
                    {Math.round(totals.production[t]).toLocaleString()}
                  </td>
                ))}
              </tr>

              <DataRow label="Daily production schedule" values={totals.daily_production} digits={0} />
              <DataRow label="Overtime days" values={totals.overtime_days} digits={0} />
              <DataRow label="Days not worked (TLO)" values={totals.days_not_worked} digits={0} />
              <DataRow label="Activity (hours)" values={totals.activity_hours} digits={1} dim />
            </tbody>
          </table>
        </div>
      </div>

      <Legend minDoh={vs.min_doh} maxDoh={vs.max_doh} changed={!!changed} />
    </>
  )
}

/* ------------------------------------------------------------------ bits */

const cellStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--border)',
  borderRight: '1px solid var(--border)',
  padding: '3px 6px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  minWidth: CELL_WIDTH,
}

function statusBg(status: Status): string {
  if (status === 'LOW') return 'var(--status-critical-bg)'
  if (status === 'HIGH') return 'var(--status-warning-bg)'
  return 'var(--status-good-bg)'
}

function Th({ children, sticky, center, dim }: { children: React.ReactNode; sticky?: boolean; center?: boolean; dim?: boolean }) {
  return (
    <th
      style={{
        position: sticky ? 'sticky' : undefined,
        left: sticky ? 0 : undefined,
        zIndex: sticky ? 2 : undefined,
        background: 'var(--surface-3)',
        borderBottom: '1px solid var(--border-strong)',
        borderRight: '1px solid var(--border)',
        padding: '5px 8px',
        textAlign: sticky ? 'left' : center ? 'center' : 'right',
        fontWeight: 600,
        fontSize: 11,
        color: dim ? 'var(--text-secondary)' : 'var(--text-primary)',
        whiteSpace: 'nowrap',
        minWidth: sticky ? LABEL_WIDTH : CELL_WIDTH,
      }}
    >
      {children}
    </th>
  )
}

function Td({ children, sticky, strong }: { children: React.ReactNode; sticky?: boolean; strong?: boolean }) {
  return (
    <td
      style={{
        ...cellStyle,
        position: sticky ? 'sticky' : undefined,
        left: sticky ? 0 : undefined,
        zIndex: sticky ? 1 : undefined,
        background: sticky ? 'var(--surface-1)' : undefined,
        textAlign: 'left',
        fontWeight: strong ? 600 : 400,
        fontFamily: 'var(--font)',
        minWidth: sticky ? LABEL_WIDTH : CELL_WIDTH,
      }}
    >
      {children}
    </td>
  )
}

function SectionRow({ label, span, crew }: { label: string; span: number; crew?: boolean }) {
  return (
    <tr className={`grid-section${crew ? ' crew' : ''}`}>
      <td colSpan={span}>{label}</td>
    </tr>
  )
}

function DataRow({ label, values, digits, dim }: { label: string; values: number[]; digits: number; dim?: boolean }) {
  return (
    <tr>
      <Td sticky>{label}</Td>
      {values.map((v, i) => (
        <td key={i} style={{ ...cellStyle, color: dim ? 'var(--text-muted)' : 'var(--text-primary)' }}>
          {digits === 0 ? Math.round(v).toLocaleString() : v.toFixed(digits)}
        </td>
      ))}
    </tr>
  )
}

function Legend({ minDoh, maxDoh, changed }: { minDoh: number; maxDoh: number; changed: boolean }) {
  return (
    <div className="card small" style={{ padding: '10px 16px', display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
      <strong>DOH status</strong>
      <StatusChip status="LOW" label={`below ${minDoh} days — shortage risk`} />
      <StatusChip status="OK" label={`${minDoh}–${maxDoh} days — target`} />
      <StatusChip status="HIGH" label={`above ${maxDoh} days — excess stock`} />
      <span className="chip neutral">· horizon edge, not judged</span>
      {changed && (
        <span className="chip warning" style={{ boxShadow: 'inset 0 0 0 1px var(--status-warning)' }}>
          outlined: working days this simulation changes
        </span>
      )}
      <span className="muted">
        A week is counted <strong>once</strong>, on the bucket it closes on — shown in bold.
      </span>
    </div>
  )
}
